import React, { useEffect, useMemo, useState } from "react";

import { getAdminExams, getAdminCourses, addExam, updateExam, deleteExam } from "../api/adminclient";
import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";
import { apiErrorMessage } from "../api/auth";

const emptyForm = { name: "", program: "CMA", courseId: "", fee: "", resultDelayHours: 48, status: "Active" };
const PAGE_SIZE = 5;

export default function AdminExams() {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    Promise.all([getAdminExams(), getAdminCourses()])
      .then(([e, c]) => {
        setExams(e.data);
        setCourses(c.data);
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not load exams.")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const courseName = (id) => courses.find((c) => String(c.id) === String(id))?.name || "—";

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (exam) => {
    setEditingId(exam.id);
    setForm({
      name: exam.name,
      program: exam.program || "CMA",
      courseId: exam.courseId ?? "",
      fee: exam.fee,
      resultDelayHours: exam.resultDelayHours ?? 48,
      status: exam.status,
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const save = async () => {
    setFormError("");
    if (!form.name || !form.courseId || !form.fee) {
      setFormError("Exam name, course, and fee are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateExam(editingId, form);
      } else {
        await addExam(form);
      }
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      setFormError(apiErrorMessage(err, "Could not save exam."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this exam? This cannot be undone.")) return;
    try {
      await deleteExam(id);
      if (editingId === id) closeModal();
      load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not delete exam."));
    }
  };

  const statusTone = (status) =>
    status === "Active" ? "active" : "inactive";

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(exams.length / PAGE_SIZE));

  useEffect(() => {
    // keep page in range if exams list shrinks/grows
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedExams = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return exams.slice(start, start + PAGE_SIZE);
  }, [exams, page]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar/>
        <h2 className="h5 fw-bold mb-3">Exams</h2>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="admin-card p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="h6 fw-bold mb-0">All Exams</h3>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" /> Create Exam
            </button>
          </div>

          {loading && <p className="text-muted mb-0">Loading exams...</p>}

          {!loading && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Exam Name</th>
                      <th>Course</th>
                      <th>Fee</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedExams.map((e, i) => (
                      <tr key={e.id} className={editingId === e.id ? "table-active" : ""}>
                        <td className="text-muted">{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="fw-semibold">{e.name}</td>
                        <td>{courseName(e.courseId)}</td>
                        <td>₹{Number(e.fee).toLocaleString("en-IN")}</td>
                        <td><span className={`admin-status-pill ${statusTone(e.status)}`}>{e.status}</span></td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(e)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(e.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {exams.length === 0 && (
                      <tr><td colSpan={6} className="text-muted small text-center py-3">No exams yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {exams.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, exams.length)} of {exams.length}
                  </span>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => goToPage(page - 1)}>
                          <i className="bi bi-chevron-left" />
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                        <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                          <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => goToPage(page + 1)}>
                          <i className="bi bi-chevron-right" />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>

        {showModal && (
          <>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title fw-bold">{editingId ? "Edit Exam" : "Create Exam"}</h5>
                    <button type="button" className="btn-close" onClick={closeModal} />
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label small text-muted">Exam Name</label>
                      <input
                        className="form-control"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-muted">Course</label>
                      <select
                        className="form-select"
                        value={form.courseId}
                        onChange={(e) => setForm((f) => ({ ...f, courseId: e.target.value }))}
                      >
                        <option value="">Select a course</option>
                        {courses.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="form-label small text-muted">Fee (INR)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={form.fee}
                          onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">Result Delay (Hours)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={form.resultDelayHours}
                          onChange={(e) => setForm((f) => ({ ...f, resultDelayHours: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small text-muted">Program</label>
                        <select
                          className="form-select"
                          value={form.program}
                          onChange={(e) => setForm((f) => ({ ...f, program: e.target.value }))}
                        >
                          <option>CMA</option>
                          <option>ACCA</option>
                          <option>EA</option>
                          <option>MQ</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">Status</label>
                        <select
                          className="form-select"
                          value={form.status}
                          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        >
                          <option>Active</option>
                          <option>Inactive</option>
                        </select>
                      </div>
                    </div>

                    {formError && <div className="text-danger small mb-0">{formError}</div>}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                    <button className="btn btn-primary" onClick={save} disabled={saving}>
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}
      </div>
    </div>
  );
}